<template>
    <v-dialog
        v-model="touched"
        max-width="480"
        persistent
    >
        <template #activator="{ on, attrs }">
            <slot v-bind="{ on, attrs }"></slot>
        </template>

        <v-card>
            <v-card-title class="customized-primary white--text">
                {{ t('import_workouts_title') }}
            </v-card-title>

            <v-card-text class="pb-0">
                <v-form class="mt-6">
                    <div class="form-group-horizontal">
                        <div class="horizontal-label">{{ t('import_workouts_browse_files') }}</div>
                        <div class="horizontal-input">
                            <v-file-input
                                v-model="form.files"
                                required
                                outlined
                                dense
                                multiple
                                prepend-icon=""
                                prepend-inner-icon="mdi-paperclip"
                                accept=".json"
                                :placeholder="t('file_manager_label_browse_files')"
                                :error-messages="form.errors.get('files.0') || form.errors.get('files')"
                                @click:clear="clearFiles"
                            ></v-file-input>
                        </div>
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    color="primary"
                    @click="submit"
                    width="45%"
                    :loading="form.busy"
                    :disabled="form.busy || !form.files || form.files.length === 0"
                >
                    {{ t('general_action_execute') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="orange"
                    @click="cancel"
                    width="45%"
                    dark
                    :disabled="form.busy"
                >
                    {{ t('general_action_cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { useForm } from '@/core/form'
import { useHttp } from '@/core/http'

export default defineComponent({
    name: 'ImportWorkoutsModal',

    setup(props, context) {
        const store = useStore()
        const http = useHttp()
        const i18n = useI18n()

        const state = reactive({
            touched: false,
        })

        // Initialize form with empty files array
        const form = reactive(useForm({
            files: []
        }))

        const submit = () => {
            // Construct FormData since we are uploading files
            const formData = new FormData()

            form.files.forEach((file: File) => {
                formData.append('files[]', file)
            })

            // Use the generic usingSubmit to handle loading state and errors automatically
            form
                .usingSubmit(
                    http.request('post', '/workouts/import', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                )
                .then(() => {
                    state.touched = false
                    form.reset()
                    // Notify admin that the background job has started
                    store.dispatch('notify', {
                        color: 'success',
                        message: i18n.t('general_notice_import_queued') || 'Import started successfully in the background!'
                    })
                    context.emit('success')
                })
        }

        const clearFiles = () => {
            form.files = []
            form.errors.clear('files.0')
            form.errors.clear('files')
        }

        const cancel = () => {
            form.reset()
            form.errors.clear()
            state.touched = false
            context.emit('cancel')
        }

        return {
            form,
            submit,
            cancel,
            clearFiles,
            ...useI18n(),
            ...toRefs(state)
        }
    }
})
</script>
